import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const BASE = process.env.REACT_APP_API_BASE || "http://localhost/twinpack/php";

const fmtFecha = (str) => {
  if (!str || str === "0000-00-00") return "—";
  const [y, m, d] = str.split(/[T ]/)[0].split("-");
  return `${d}/${m}/${y}`;
};

const fmtMonto = (n) =>
  n != null ? `$${parseFloat(n).toLocaleString("es-AR", { minimumFractionDigits: 2 })}` : "—";

const S = {
  page: { padding: "28px 20px", fontFamily: "'Segoe UI', system-ui, sans-serif", color: "#121212" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 },
  title: { fontSize: 18, fontWeight: 700, margin: 0 },
  search: { padding: "8px 12px", border: "1.5px solid #e0d6dc", borderRadius: 8, fontSize: 13, outline: "none", minWidth: 220 },
  stats: { fontSize: 13, color: "#888", marginBottom: 16 },
  table: { width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: 10, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.07)" },
  th: { background: "#fdf0f6", padding: "10px 14px", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#9f0059", textAlign: "left", borderBottom: "1.5px solid #f0e6ec" },
  td: { padding: "10px 14px", fontSize: 13, borderBottom: "1px solid #f7f4f6", verticalAlign: "middle" },
  badge: (recibida) => ({
    display: "inline-block", padding: "2px 9px", borderRadius: 20, fontSize: 11, fontWeight: 600,
    background: recibida === "1" || recibida === 1 ? "#f0fdf4" : "#eff6ff",
    color:      recibida === "1" || recibida === 1 ? "#166534" : "#1d4ed8",
    border:     `1.5px solid ${recibida === "1" || recibida === 1 ? "#bbf7d0" : "#bfdbfe"}`,
  }),
  deleteBtn: { padding: "4px 12px", background: "none", border: "1.5px solid #dc2626", borderRadius: 6, color: "#dc2626", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  confirmRow: { display: "flex", alignItems: "center", gap: 6 },
  confirmYes: { padding: "4px 10px", background: "#dc2626", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" },
  confirmNo:  { padding: "4px 10px", background: "none", color: "#555", border: "1.5px solid #d1d5db", borderRadius: 6, fontSize: 12, cursor: "pointer" },
  empty: { textAlign: "center", padding: "40px 0", color: "#aaa", fontSize: 14 },
};

const OrdenesAdmin = () => {
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [confirmId, setConfirmId] = useState(null);

  const loadOrdenes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${BASE}/admin_ordenes_listar.php`, new URLSearchParams({}));
      setOrdenes(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Error al cargar órdenes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOrdenes(); }, [loadOrdenes]);

  const handleEliminar = async (id) => {
    try {
      const fd = new FormData();
      fd.append("id", id);
      const res = await axios.post(`${BASE}/eliminar_orden.php`, fd);
      if (res.data?.status === "success") {
        toast.success("Orden eliminada");
        setConfirmId(null);
        loadOrdenes();
      } else {
        toast.error("No se pudo eliminar la orden");
      }
    } catch {
      toast.error("Error de conexión");
    }
  };

  const filtradas = ordenes.filter((o) => {
    const q = busqueda.toLowerCase();
    return (
      !q ||
      String(o.id).includes(q) ||
      (o.cliente_nombre || "").toLowerCase().includes(q) ||
      (o.proveedor_nombre || "").toLowerCase().includes(q) ||
      (o.observaciones || "").toLowerCase().includes(q)
    );
  });

  return (
    <div style={S.page}>
      <div style={S.header}>
        <h2 style={S.title}>Gestión de Órdenes</h2>
        <input
          style={S.search}
          placeholder="Buscar por cliente, proveedor, nº…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
      </div>

      <div style={S.stats}>
        {loading ? "Cargando…" : `${filtradas.length} orden${filtradas.length !== 1 ? "es" : ""} encontrada${filtradas.length !== 1 ? "s" : ""} de ${ordenes.length} totales`}
      </div>

      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>#</th>
            <th style={S.th}>Cliente</th>
            <th style={S.th}>Proveedor</th>
            <th style={S.th}>Ítems</th>
            <th style={S.th}>Monto</th>
            <th style={S.th}>F. Entrega</th>
            <th style={S.th}>Creada</th>
            <th style={S.th}>Tipo</th>
            <th style={S.th}>Acción</th>
          </tr>
        </thead>
        <tbody>
          {!loading && filtradas.length === 0 && (
            <tr><td colSpan={9} style={S.empty}>No hay órdenes.</td></tr>
          )}
          {filtradas.map((o) => (
            <tr key={o.id} style={{ background: confirmId === o.id ? "#fef2f2" : "transparent" }}>
              <td style={{ ...S.td, fontWeight: 700, color: "#9f0059" }}>#{o.id}</td>
              <td style={S.td}>{o.cliente_nombre || "—"}</td>
              <td style={S.td}>{o.proveedor_nombre || "—"}</td>
              <td style={{ ...S.td, textAlign: "center" }}>{o.cantidad_items}</td>
              <td style={{ ...S.td, fontWeight: 600 }}>{fmtMonto(o.monto_total)}</td>
              <td style={S.td}>{fmtFecha(o.fecha_entrega)}</td>
              <td style={{ ...S.td, color: "#888" }}>{fmtFecha(o.created_at)}</td>
              <td style={S.td}>
                <span style={S.badge(o.recibida)}>
                  {o.recibida === "1" || o.recibida === 1 ? "OC" : "Cotización"}
                </span>
              </td>
              <td style={S.td}>
                {confirmId === o.id ? (
                  <div style={S.confirmRow}>
                    <span style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>¿Eliminar?</span>
                    <button style={S.confirmYes} onClick={() => handleEliminar(o.id)}>Sí</button>
                    <button style={S.confirmNo} onClick={() => setConfirmId(null)}>No</button>
                  </div>
                ) : (
                  <button style={S.deleteBtn} onClick={() => setConfirmId(o.id)}>Eliminar</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default OrdenesAdmin;
